import * as React from "react";
import { useCallback, useState } from "react";
import { Accordion, AccordionHeader, AccordionItem, AccordionPanel } from "@fluentui/react-components";
import { CoObjectSummary } from "../interfaces/CoObject";
import { formatMessage } from "../../utils/helpers/LanguageHelper";
import { ConsistencyTagInfo } from "./RulesResultPanel";
import { PILL_BASE_STYLE } from "../generic/TrackedChangeOperationTypePill";
import { TruncatedText } from "../generic/truncatedText/TruncatedText";

interface ConsistencyResultContentProps {
  consistencyTags: ConsistencyTagInfo[];
  onShowOnDocument?: (payload: CoObjectSummary) => void;
}

/** Group objects by their display value text, sorted alphabetically */
function groupByValue(objects: CoObjectSummary[]): { value: string; objects: CoObjectSummary[] }[] {
  const map = new Map<string, CoObjectSummary[]>();
  for (const obj of objects ?? []) {
    const key = obj.text ?? obj.id;
    if (!map.has(key)) map.set(key, []);
    map.get(key).push(obj);
  }
  return Array.from(map.entries())
    .sort(([a], [b]) => a.localeCompare(b))
    .map(([value, objs]) => ({ value, objects: objs }));
}

const TagPill: React.FC<{ label: string; inconsistent?: boolean }> = ({ label, inconsistent }) => (
  <span
    style={{
      ...PILL_BASE_STYLE,
      backgroundColor: "var(--color-blue)",
      minWidth: 0,
      whiteSpace: "nowrap",
      textOverflow: "ellipsis",
      ...(inconsistent && { border: "2px solid var(--colorPaletteRedBorder2, #c50f1f)" }),
    }}
  >
    <TruncatedText
      text={label}
      style={{
        color: "white",
        fontSize: 12,
        fontWeight: 600,
        minWidth: 0,
        maxWidth: "100%",
      }}
    />
  </span>
);

/**
 * Shows a "Show on document" link initially.
 * Once clicked, switches to a `< N >` navigator to cycle through objects.
 */
const ObjectNavigator: React.FC<{
  objects: CoObjectSummary[];
  onShowOnDocument?: (obj: CoObjectSummary) => void;
}> = ({ objects, onShowOnDocument }) => {
  const navigable = objects.filter((o) => o.contentControlId != null);
  const [activeIndex, setActiveIndex] = useState<number | null>(null);

  const goTo = useCallback(
    (index: number) => {
      setActiveIndex(index);
      onShowOnDocument?.(navigable[index]);
    },
    [navigable, onShowOnDocument]
  );

  if (navigable.length === 0) return null;

  /* Single object: always show a plain link, no navigator */
  if (navigable.length === 1) {
    return (
      <div style={{ display: "flex", justifyContent: "flex-end" }}>
        <button
          type="button"
          onClick={(e) => {
            e.preventDefault();
            onShowOnDocument?.(navigable[0]);
          }}
          style={{ color: "#0a66c2", fontSize: 12, textDecoration: "underline", whiteSpace: "nowrap", background: "none", border: "none", padding: 0, cursor: "pointer" }}
        >
          {formatMessage("cof.validation.link.showondocument")}
        </button>
      </div>
    );
  }

  if (activeIndex === null) {
    return (
      <div style={{ display: "flex", justifyContent: "flex-end" }}>
        <button
          type="button"
          onClick={(e) => {
            e.preventDefault();
            goTo(0);
          }}
          style={{ color: "#0a66c2", fontSize: 12, textDecoration: "underline", whiteSpace: "nowrap", background: "none", border: "none", padding: 0, cursor: "pointer" }}
        >
          {formatMessage("cof.validation.link.showondocument")}
        </button>
      </div>
    );
  }

  const navBtnStyle: React.CSSProperties = {
    background: "none",
    border: "none",
    cursor: "pointer",
    color: "#0a66c2",
    fontSize: 13,
    fontWeight: 600,
    padding: "0 2px",
    lineHeight: 1,
  };

  return (
    <div style={{ display: "flex", justifyContent: "flex-end", alignItems: "center", gap: 2 }}>
      <button
        style={{ ...navBtnStyle, opacity: activeIndex === 0 ? 0.3 : 1 }}
        disabled={activeIndex === 0}
        onClick={() => goTo(activeIndex - 1)}
        aria-label="Previous"
      >
        {"<"}
      </button>
      <span style={{ fontSize: 12, minWidth: 20, textAlign: "center", color: "#111827" }}>{activeIndex + 1}</span>
      <button
        style={{ ...navBtnStyle, opacity: activeIndex === navigable.length - 1 ? 0.3 : 1 }}
        disabled={activeIndex === navigable.length - 1}
        onClick={() => goTo(activeIndex + 1)}
        aria-label="Next"
      >
        {">"}
      </button>
    </div>
  );
};

const SectionHeader: React.FC<{ title: string; count: number }> = ({ title, count }) => (
  <div style={{ fontWeight: 700, fontSize: 13, color: "#374151" }}>
    {title} ({count})
  </div>
);

export const ConsistencyResultContent: React.FC<ConsistencyResultContentProps> = ({
  consistencyTags,
  onShowOnDocument,
}) => {
  const [openTags, setOpenTags] = useState<string[]>([]);

  const failedTags = consistencyTags.filter((t) => t.status === "FAILED" || t.status === "ERROR");
  const verifiedTags = consistencyTags.filter((t) => t.status === "VERIFIED");

  return (
    <div style={{ display: "flex", flexDirection: "column", paddingBottom: 8 }}>
      {/* ── Inconsistent tags ── */}
      {failedTags.length > 0 && (
        <div>
          <SectionHeader title="Inconsistent tag" count={failedTags.length} />
          <Accordion
            multiple
            collapsible
            openItems={openTags}
            onToggle={(_e, data) => setOpenTags(data.openItems as string[])}
            className="rrp-consistency"
          >
            <style>{`
              .rrp-consistency .fui-AccordionHeader__button { padding-left: 0; }
            `}</style>
            {failedTags.map((tag, idx) => {
              const itemKey = `${idx}_${tag.label}`;
              const isExpanded = openTags.includes(itemKey);
              return (
                <AccordionItem
                  key={itemKey}
                  value={itemKey}
                  className={`alp-item${isExpanded ? " alp-expanded" : ""}`}
                  data-expanded={isExpanded ? "true" : "false"}
                >
                  <AccordionHeader size="small" expandIconPosition="end">
                    <TagPill label={tag.label} inconsistent />
                  </AccordionHeader>

                  <AccordionPanel style={{ padding: 0, margin: 0 }}>
                    <div style={{ padding: "0 0 4px" }}>
                      <hr style={{ borderTop: "1px solid #e5e7eb", margin: "0" }} />

                      {tag.status === "ERROR" && tag.errorMessage ? (
                        /* ERROR → single row: error message */
                        <div style={{ fontSize: 13, color: "#b3261e" }}>{tag.errorMessage}</div>
                      ) : (
                        /* FAILED → grouped by value; title bold + count; navigator right */
                        groupByValue(tag.objects).map((group) => (
                          <div
                            key={group.value}
                            style={{
                              display: "flex",
                              justifyContent: "space-between",
                              alignItems: "center",
                              marginBottom: 4,
                              gap: 4,
                              minWidth: 0,
                            }}
                          >
                            <div
                              style={{
                                fontWeight: 700,
                                fontSize: 13,
                                color: "#111827",
                                minWidth: 0,
                                flex: 1,
                                display: "flex",
                                alignItems: "center",
                                gap: 4,
                                overflow: "hidden",
                              }}
                            >
                              <TruncatedText
                                text={group.value}
                                style={{ fontWeight: 700, fontSize: 13, color: "#111827" }}
                              />
                              <span style={{ whiteSpace: "nowrap" }}>({group.objects.length})</span>
                            </div>
                            <ObjectNavigator objects={group.objects} onShowOnDocument={onShowOnDocument} />
                          </div>
                        ))
                      )}
                    </div>
                  </AccordionPanel>
                </AccordionItem>
              );
            })}
          </Accordion>
        </div>
      )}

      {/* ── Consistent tags ── */}
      {verifiedTags.length > 0 && (
        <div>
          <SectionHeader title="Consistent tag" count={verifiedTags.length} />
          <div style={{ display: "flex", flexWrap: "wrap", gap: 4, padding: "7px 0" }}>
            {verifiedTags.map((tag) => (
              <TagPill key={tag.label} label={tag.label} />
            ))}
          </div>
        </div>
      )}
    </div>
  );
};
